import React from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClipboardList, 
  faCheckCircle, 
  faClock,
  faPercentage,
  faChartBar
} from '@fortawesome/free-solid-svg-icons';

interface TemplatePerformance {
  _id: string;
  templateTitle: string;
  templateType: string;
  totalSent: number;
  completed: number;
  inProgress?: number;
  sent?: number;
  completionRate: number;
  avgProgress: number;
  avgCompletionTime: number;
}

interface TemplatePerformanceTableProps {
  data: TemplatePerformance[];
}

const TemplatePerformanceTable: React.FC<TemplatePerformanceTableProps> = ({ data }) => {
  const getTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      producer: 'Produzent',
      trader: 'Händler',
      trader_producer: 'Händler & Produzent',
      non_food: 'Non-Food',
      gmo_declaration: 'GVO-Erklärung',
      conformity_declaration: 'Konformitätserklärung'
    };
    return types[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: string } = {
      producer: 'bg-emerald-100 text-emerald-800',
      trader: 'bg-blue-100 text-blue-800',
      trader_producer: 'bg-purple-100 text-purple-800',
      non_food: 'bg-indigo-100 text-indigo-800',
      gmo_declaration: 'bg-amber-100 text-amber-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  const getCompletionColor = (rate: number) => {
    if (rate >= 80) return 'bg-emerald-500';
    if (rate >= 60) return 'bg-yellow-500';
    if (rate >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'text-emerald-600';
    if (progress >= 60) return 'text-yellow-600';
    if (progress >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatCompletionTime = (days: number) => {
    if (!days || days < 1) return 'N/A';
    const roundedDays = Math.round(days);
    return `${roundedDays}d`;
  };

  const getStatusBreakdown = (template: TemplatePerformance) => {
    const waiting = (template.sent || 0);
    const inProgress = (template.inProgress || 0);
    const completed = template.completed;
    
    return { waiting, inProgress, completed };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
    >
      {/* **HEADER** */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 border-b border-gray-200">
        <div className="flex items-center">
          <div className="bg-purple-500 p-3 rounded-lg mr-4">
            <FontAwesomeIcon icon={faClipboardList} className="text-white text-lg" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900">Template Performance</h3>
            <p className="text-sm text-gray-600 mt-1">Detaillierte Leistungsanalyse aller Fragebogen-Templates</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {data.length > 0 ? (
          <div className="space-y-4">
            {data.map((template, index) => {
              const statusBreakdown = getStatusBreakdown(template);
              const completionPercentage = template.completionRate;
              
              return (
                <motion.div
                  key={template._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  {/* **TEMPLATE HEADER** */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center">
                      <h4 className="text-lg font-semibold text-gray-900 mr-3">
                        {template.templateTitle}
                      </h4>
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getTypeColor(template.templateType)}`}>
                        {getTypeLabel(template.templateType)}
                      </span>
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-gray-900">{template.totalSent}</div>
                      <div className="text-xs text-gray-600">Gesamt gesendet</div>
                    </div>
                  </div>

                  {/* **PROGRESS BAR** */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Erfolgsrate</span>
                      <span className="text-sm font-bold text-gray-900">{Math.round(completionPercentage)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${completionPercentage}%` }}
                        transition={{ duration: 1, delay: index * 0.1 }}
                        className={`h-3 rounded-full ${getCompletionColor(completionPercentage)}`}
                      />
                    </div>
                  </div>

                  {/* **STATISTICS GRID** */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {/* **STATUS BREAKDOWN** */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faChartBar} className="text-blue-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Status</span>
                      </div>
                      <div className="space-y-1">
                        {statusBreakdown.waiting > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Wartend:</span>
                            <span className="font-medium text-blue-600">{statusBreakdown.waiting}</span>
                          </div>
                        )}
                        {statusBreakdown.inProgress > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-600">Bearbeitung:</span>
                            <span className="font-medium text-yellow-600">{statusBreakdown.inProgress}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Fertig:</span>
                          <span className="font-medium text-emerald-600">{statusBreakdown.completed}</span>
                        </div>
                      </div>
                    </div>

                    {/* **COMPLETION RATE** */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Erfolg</span>
                      </div>
                      <div className="text-xl font-bold text-emerald-600">
                        {Math.round(completionPercentage)}%
                      </div>
                      <div className="text-xs text-gray-600">
                        {template.completed} von {template.totalSent}
                      </div>
                    </div>

                    {/* **AVERAGE PROGRESS** */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faPercentage} className="text-purple-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Ø Fortschritt</span>
                      </div>
                      <div className={`text-xl font-bold ${getProgressColor(template.avgProgress)}`}>
                        {Math.round(template.avgProgress || 0)}%
                      </div>
                      <div className="text-xs text-gray-600">
                        Durchschnittlich
                      </div>
                    </div>

                    {/* **COMPLETION TIME** */}
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <FontAwesomeIcon icon={faClock} className="text-amber-500 mr-2" />
                        <span className="text-sm font-medium text-gray-700">Ø Zeit</span>
                      </div>
                      <div className="text-xl font-bold text-amber-600">
                        {formatCompletionTime(template.avgCompletionTime)}
                      </div>
                      <div className="text-xs text-gray-600">
                        Bearbeitungszeit
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faClipboardList} className="text-gray-300 text-5xl mb-4" />
            <p className="text-gray-600 font-medium text-lg">Keine Template-Daten verfügbar</p>
            <p className="text-gray-500 text-sm mt-2">Erstellen Sie Fragebögen und senden Sie diese an Lieferanten</p>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default TemplatePerformanceTable;